import httpClient from './httpClient';

export interface GdStartResponse {
  success: boolean;
  message?: string;
  session_id: string;
  mongodb_session_id?: string;
  topic?: string;
  topic_description?: string;
  token_info?: {
    tokens_consumed?: number;
    remaining_quota?: number;
  };
  websocket_url?: string;
}

export interface GdStatusResponse {
  success: boolean;
  session?: {
    session_id: string;
    topic?: string;
    topic_description?: string;
    active?: boolean;
    status?: any;
  } | null;
}

export interface GdFeedbackResponse {
  success: boolean;
  feedback?: any;
  topic?: string;
  session_id?: string;
}

// ─── Session Feedback types ──────────────────────────────────────────

export interface GdDimensionScore {
  score: number;
  rationale: string;
}

export interface GdSessionFeedbackEvaluation {
  success: boolean;
  dimensions: Record<string, GdDimensionScore>;
  overall_score: number;
  readiness_level: string;
  performance_summary: string;
  strengths: string[];
  areas_for_improvement: string[];
  critical_issues: string[];
  communication_skills: {
    clarity: number;
    confidence: number;
    engagement: number;
    feedback?: string;
    clarity_feedback?: string;
    confidence_feedback?: string;
    engagement_feedback?: string;
  };
  content_quality: { relevance: number; depth: number; examples: number; feedback: string };
  gd_specific_skills: { topic_adherence: number; counter_argument_handling: number; group_awareness: number; feedback: string };
  participation_metrics: { total_user_turns: number; contribution_ratio: string; rounds_active_in: number[]; interaction_pattern: string };
  key_highlights: { point: string; tags: string[]; summary: string }[];
  recommendations: string[];
  error: string | null;
}

export interface GdSessionFeedbackResponse {
  success: boolean;
  session_type: string;
  session: {
    session_id: string;
    topic: string;
    description: string;
    difficulty: string;
    total_turns: number;
    moderator_final_remarks: string;
    participants: Record<string, { name: string; personality: string }>;
  };
  evaluation: GdSessionFeedbackEvaluation;
  timestamp: string;
}

const cleanPath = (path: string) => path.replace(/^\/+/, '');

export const gdApi = {
  start: async (token: string, topic_id?: string, description?: string): Promise<GdStartResponse> => {
    // Send the user's display name as both `description` (legacy) and `user_name` (explicit)
    const response = await httpClient.post(cleanPath('/gd/start'), { token, topic_id, description, user_name: description });
    return response.data;
  },
  status: async (): Promise<GdStatusResponse> => {
    const response = await httpClient.get(cleanPath('/gd/status'));
    return response.data;
  },
  feedback: async (): Promise<GdFeedbackResponse> => {
    const response = await httpClient.get(cleanPath('/gd/feedback'));
    return response.data;
  },
  sessionFeedback: async (sessionId: string): Promise<GdSessionFeedbackResponse> => {
    const response = await httpClient.get(
      cleanPath(`/orchestrator/session/feedback/${encodeURIComponent(sessionId)}`)
    );
    return response.data;
  },
  stop: async (): Promise<void> => {
    // Optional helper if backend exposes a stop route; swallow 404 for compatibility
    try {
      await httpClient.post(cleanPath('/gd/stop'));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status && status !== 404) {
        console.warn('GD stop request failed (continuing):', err);
      }
    }
  },
  abandon: async (): Promise<void> => {
    try {
      await httpClient.post(cleanPath('/gd/abandon'));
    } catch (err) {
      console.warn('GD abandon request failed:', err);
    }
  },
};

export default gdApi;
